/**
 * Provider-agnostic marketplace item identity for unmapped-product mapping.
 * Names always come from provider/order evidence, never from Tradivo candidates.
 */

export type MarketplaceItemIdentity = {
  productName: string | null;
  variantLabel: string | null;
  sku: string | null;
  externalProductId: string | null;
  externalVariantId: string | null;
  quantity: number | null;
  imageUrl: string | null;
  externalOrderId: string | null;
  optionValues: Record<string, string>;
  source: "marketplace_item" | "line" | "sku_only";
};

export type MappingSuggestionKind =
  | "exact_sku"
  | "external_identifier"
  | "barcode_gtin"
  | "name_similarity";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value).trim();
  return s ? s : null;
}

function qty(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function optionLabelFromUnknown(raw: unknown): { label: string | null; values: Record<string, string> } {
  const values: Record<string, string> = {};
  if (Array.isArray(raw)) {
    for (const row of raw) {
      const rec = asRecord(row);
      if (!rec) continue;
      const name = text(rec.display_name ?? rec.name ?? rec.option) ?? "Option";
      const value = text(rec.display_value ?? rec.value ?? rec.option_value);
      if (value) values[name] = value;
    }
  } else {
    const rec = asRecord(raw);
    if (rec) {
      for (const [k, v] of Object.entries(rec)) {
        const value = text(v);
        if (value) values[k] = value;
      }
    }
  }
  const label = Object.values(values).filter(Boolean).join(" / ") || null;
  return { label, values };
}

export function extractMarketplaceItemIdentity(opts: {
  entityRef?: string | null;
  externalData?: unknown;
}): MarketplaceItemIdentity {
  const data = asRecord(opts.externalData) ?? {};
  const canonical = asRecord(data.marketplace_item);
  const line = asRecord(data.line) ?? {};
  const sku = text(canonical?.sku) ?? text(line.sku) ?? text(opts.entityRef);
  const optionsRaw = canonical?.product_options ?? canonical?.option_values ?? line.product_options ?? line.option_values ?? line.options;
  const options = optionLabelFromUnknown(optionsRaw);
  const productName =
    text(canonical?.product_name) ??
    text(canonical?.title) ??
    text(line.title) ??
    text(line.product_name) ??
    text(line.name);
  const variantLabel =
    text(canonical?.variant_label) ??
    text(line.variant_label) ??
    text(line.variant_title) ??
    options.label;
  const source: MarketplaceItemIdentity["source"] = canonical
    ? "marketplace_item"
    : productName || variantLabel
      ? "line"
      : "sku_only";
  const orderIds = Array.isArray(data.external_order_ids) ? data.external_order_ids : [];
  const orderId = text(data.external_order_id) ?? text(orderIds[0]);
  return {
    productName,
    variantLabel,
    sku,
    externalProductId: text(canonical?.product_id) ?? text(line.product_id),
    externalVariantId: text(canonical?.variant_id) ?? text(line.variant_id),
    quantity: qty(canonical?.qty ?? canonical?.quantity) ?? qty(line.qty ?? line.quantity),
    imageUrl: text(canonical?.image_url) ?? text(line.image_url) ?? text(line.thumbnail),
    externalOrderId: orderId,
    optionValues: options.values,
    source,
  };
}

/** Headline for the inbox. Never invents a Tradivo catalogue name. */
export function unmappedProductHeadline(item: MarketplaceItemIdentity): string {
  return item.productName || (item.sku ? `SKU ${item.sku}` : "Unmapped marketplace item");
}

export function mappingSuggestionIsAutomaticAuthority(kind: MappingSuggestionKind | null | undefined): boolean {
  return kind === "exact_sku" || kind === "external_identifier" || kind === "barcode_gtin";
}

/** Fuzzy title is suggestion-only. Exact identifier matches still require operator confirm in this UI. */
export function fuzzyTitleMayAutoMap(): false {
  return false;
}

export function nameSimilarityScore(a: string, b: string): number {
  const tokens = (s: string) =>
    s
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 1);
  const left = new Set(tokens(a));
  const right = tokens(b);
  if (left.size === 0 || right.length === 0) return 0;
  let hit = 0;
  for (const t of right) if (left.has(t)) hit += 1;
  return hit / Math.max(left.size, right.length);
}

export function mappingSuggestionKind(opts: {
  externalSku?: string | null;
  candidateSku?: string | null;
  hasConnectorIdentifier?: boolean;
  barcodeOrGtinMatch?: boolean;
  externalProductName?: string | null;
  candidateProductName?: string | null;
}): MappingSuggestionKind | null {
  const extSku = (opts.externalSku ?? "").trim().toLowerCase();
  const candSku = (opts.candidateSku ?? "").trim().toLowerCase();
  if (extSku && candSku && extSku === candSku) return "exact_sku";
  if (opts.hasConnectorIdentifier) return "external_identifier";
  if (opts.barcodeOrGtinMatch) return "barcode_gtin";
  const name = (opts.externalProductName ?? "").trim();
  const cand = (opts.candidateProductName ?? "").trim();
  if (name && cand && nameSimilarityScore(name, cand) >= 0.45) return "name_similarity";
  return null;
}

export function mappingSuggestionLabel(kind: MappingSuggestionKind | null): string | null {
  if (kind === "exact_sku") return "Exact SKU";
  if (kind === "external_identifier") return "Known channel identifier";
  if (kind === "barcode_gtin") return "Barcode / GTIN";
  if (kind === "name_similarity") return "Possible match";
  return null;
}
